// ABOUTME: Renders WhatsApp application flow controls for job configuration screens.
// ABOUTME: Lets recruiters publish a chatbot flow and manage the job-specific wa.me link.
import React from "react";
import {
    Box,
    Button,
    Checkbox,
    Chip,
    Divider,
    FormControlLabel,
    Grid,
    IconButton,
    MenuItem,
    Paper,
    Switch,
    TextField,
    Tooltip,
    Typography
} from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import DeleteIcon from "@material-ui/icons/Delete";
import DragIndicatorIcon from "@material-ui/icons/DragIndicator";
import FileCopyIcon from "@material-ui/icons/FileCopy";
import HelpOutlineIcon from "@material-ui/icons/HelpOutline";
import WhatsAppIcon from "@material-ui/icons/WhatsApp";
import {
    DragDropContext,
    Draggable,
    DraggableProvided,
    DraggableStateSnapshot,
    Droppable,
    DroppableProvided,
    DropResult
} from "react-beautiful-dnd";
import { Alert } from "@material-ui/lab";
import { apiDirectCall } from "customHooks/useApi";
import useShowMessage from "customHooks/useShowMessage/useShowMessage";
import { useAuthState } from "_ReactContext/AuthContext";
import { getWhatsappApiResponseData } from "functions/whatsappApiResponse";
import {
    WHATSAPP_APPLICATION_CONDITION_OPERATORS,
    WHATSAPP_APPLICATION_NODE_TYPES,
    WhatsappApplicationCondition,
    WhatsappApplicationFlow,
    WhatsappApplicationNode,
    WhatsappApplicationNodeType,
    applyWhatsappApplicationModelFlow,
    buildDefaultWhatsappApplicationFlow,
    buildDefaultWhatsappPreRegistrationFlow,
    canPublishWhatsappApplicationFlow,
    fillWhatsappApplicationMessageTexts,
    createWhatsappApplicationNodeEditorKey,
    getNextWhatsappApplicationNodeId,
    getSelectableWhatsappApplicationModels,
    getWhatsappApplicationMessageTimeout,
    getWhatsappApplicationModelIdForSave,
    isWhatsappApplicationFlowOperationPending,
    normalizeWhatsappApplicationFlow,
    reorderWhatsappApplicationFlowNodes,
    serializeWhatsappApplicationFlowForSave,
    publishWhatsappApplicationFlowDraft,
    resolveWhatsappApplicationErrorMessage,
    toggleWhatsappApplicationLinkActive
} from "./helpers";
import {
    WhatsappApplicationOptionsEditorState,
    applyWhatsappApplicationLinkSettings,
    buildWhatsappApplicationOptionsDraftByEditorKey,
    canonicalizeWhatsappApplicationOptionsDraft,
    formatWhatsappApplicationOptions,
    getWhatsappPreRegistrationFieldPresentation,
    removeWhatsappApplicationOptionsNode,
    updateWhatsappApplicationOptionsDraft
} from "./WhatsappApplicationPanel.helpers";
import WhatsappApplicationSummary from "./WhatsappApplicationSummary";
import WhatsappApplicationHelpDialog from "./WhatsappApplicationHelpDialog";
import { whatsappPreRegistrationTemplatePlaceholderValues } from "./whatsappPreRegistrationTemplatePlaceholderValues";

interface IWhatsappApplicationPanel {
    t: any;
    classes?: any;
    jobId?: string;
    jobTitle?: string;
    modelId?: string;
    configurationScope?: "job" | "model";
    modelScope?: "JOB" | "PRE_REGISTRATION";
    pipeline?: Compleo.IObject;
    screeningQuestionnaire?: Compleo.IObject;
    enableScreeningQuestionnaire?: boolean;
    readonly?: boolean;
    mode?: "editor" | "summary";
    refreshKey?: number;
    onEdit?: () => void;
    onUpdated?: () => void;
    onModelSaved?: (flow: WhatsappApplicationFlow) => void;
}

const getStageOptions = (
    pipeline?: Compleo.IObject,
    stageUsage: "qualified" | "notQualified" = "qualified"
) =>
    (pipeline?.stages || [])
        .filter((stage: Compleo.IObject) => {
            const typeValue = String(stage?.type?.value || stage?.type || "");
            const rejected = typeValue === "rejected";
            return stageUsage === "notQualified" ? rejected : !rejected;
        })
        .map((stage: Compleo.IObject) => ({
            value: String(stage.id || ""),
            label: String(stage.name || stage.id || "")
        }))
        .filter((stage: Compleo.IObject) => stage.value);

const WHATSAPP_APPLICATION_INTERACTIVE_MESSAGE_TEXT_MAX_LENGTH = 768;
const WHATSAPP_APPLICATION_TEXT_MESSAGE_MAX_LENGTH = 4096;
const WHATSAPP_APPLICATION_NODES_DROPPABLE_ID = "whatsapp-application-nodes";

type WhatsappApplicationEditorState = WhatsappApplicationOptionsEditorState<
    WhatsappApplicationFlow
>;

type WhatsappApplicationFlowUpdate =
    | WhatsappApplicationFlow
    | ((currentFlow: WhatsappApplicationFlow) => WhatsappApplicationFlow);

const getWhatsappApplicationNodeTypeLabelKey = (
    nodeType: WhatsappApplicationNodeType,
    configurationScope: "job" | "model",
    hasScreeningQuestionnaire: boolean
) =>
    nodeType === "screeningQuestionnaire" &&
    configurationScope === "job" &&
    !hasScreeningQuestionnaire
        ? "whatsappApplicationNodeType_screeningQuestionnaireUnavailable"
        : `whatsappApplicationNodeType_${nodeType}`;

const WhatsappApplicationPanel = (props: IWhatsappApplicationPanel) => {
    const {
        t,
        classes,
        jobId = "",
        jobTitle = "",
        modelId = "",
        configurationScope = "job",
        modelScope = "JOB",
        pipeline,
        screeningQuestionnaire,
        enableScreeningQuestionnaire,
        readonly,
        mode = "editor",
        refreshKey,
        onEdit,
        onUpdated,
        onModelSaved
    } = props;
    const { company } = useAuthState();
    const showMessage = useShowMessage();
    const showMessageRef = React.useRef(showMessage);
    const loadRequestRef = React.useRef(0);
    const editorRevisionRef = React.useRef(0);
    const companyId = Number(company.companyId || 0);
    const qualifiedStageOptions = React.useMemo(
        () => getStageOptions(pipeline, "qualified"),
        [pipeline]
    );
    const notQualifiedStageOptions = React.useMemo(
        () => getStageOptions(pipeline, "notQualified"),
        [pipeline]
    );
    const hasScreeningQuestionnaire =
        enableScreeningQuestionnaire === true &&
        (screeningQuestionnaire?.questions || []).length > 0;
    const enabled =
        company.whatsappEnabled === true &&
        company.whatsappApplicationEnabled === true;
    const defaultFlowTexts = React.useMemo(
        () => ({
            applicationStartText: t(
                "whatsappApplicationDefaultApplicationStartText"
            ),
            existingCandidateFoundText: t(
                "whatsappApplicationDefaultExistingCandidateFoundText"
            ),
            existingCandidateInvalidChoiceText: t(
                "whatsappApplicationDefaultExistingCandidateInvalidChoiceText"
            ),
            consentInvalidAnswerText: t(
                "whatsappApplicationDefaultConsentInvalidAnswerText"
            ),
            resumeInvalidAnswerText: t(
                "whatsappApplicationDefaultResumeInvalidAnswerText"
            ),
            consentText: t("whatsappApplicationDefaultConsentText"),
            questionText: t("whatsappApplicationDefaultQuestionText"),
            qualifiedText: t("whatsappApplicationDefaultQualifiedText"),
            notQualifiedText: t("whatsappApplicationDefaultNotQualifiedText"),
            cvParsingStartedText: t(
                "whatsappApplicationDefaultCvParsingStartedText"
            ),
            cvParsingCompletedText: t(
                "whatsappApplicationDefaultCvParsingCompletedText"
            )
        }),
        [t]
    );
    const preRegistrationDefaultFlowTexts = React.useMemo(
        () => ({
            name: t("whatsappPreRegistrationDefaultFlowName"),
            applicationStartText: t(
                "whatsappPreRegistrationDefaultApplicationStartText"
            ),
            consentText: t("whatsappPreRegistrationDefaultConsentText"),
            candidateNameText: t(
                "whatsappPreRegistrationDefaultCandidateNameText"
            ),
            dateOfBirthText: t("whatsappPreRegistrationDefaultDateOfBirthText"),
            underageText: t("whatsappPreRegistrationDefaultUnderageText"),
            positionText: t("whatsappPreRegistrationDefaultPositionText"),
            availabilityText: t(
                "whatsappPreRegistrationDefaultAvailabilityText"
            ),
            morningOnlyText: t("whatsappPreRegistrationDefaultMorningOnlyText"),
            resumeText: t("whatsappPreRegistrationDefaultResumeText"),
            eligibleText: t("whatsappPreRegistrationDefaultEligibleText"),
            endText: t("whatsappPreRegistrationDefaultEndText"),
            existingCandidateFoundText: t(
                "whatsappApplicationDefaultExistingCandidateFoundText"
            ),
            existingCandidateInvalidChoiceText: t(
                "whatsappApplicationDefaultExistingCandidateInvalidChoiceText"
            ),
            consentInvalidAnswerText: t(
                "whatsappApplicationDefaultConsentInvalidAnswerText"
            ),
            resumeInvalidAnswerText: t(
                "whatsappApplicationDefaultResumeInvalidAnswerText"
            ),
            cvParsingStartedText: t(
                "whatsappApplicationDefaultCvParsingStartedText"
            ),
            cvParsingCompletedText: t(
                "whatsappApplicationDefaultCvParsingCompletedText"
            )
        }),
        [t]
    );
    const buildDefaultFlow = React.useCallback(
        () =>
            modelScope === "PRE_REGISTRATION"
                ? buildDefaultWhatsappPreRegistrationFlow(
                      preRegistrationDefaultFlowTexts
                  )
                : buildDefaultWhatsappApplicationFlow(
                      jobTitle,
                      defaultFlowTexts
                  ),
        [
            defaultFlowTexts,
            jobTitle,
            modelScope,
            preRegistrationDefaultFlowTexts
        ]
    );
    const [loading, setLoading] = React.useState(false);
    const [saving, setSaving] = React.useState(false);
    const [publishing, setPublishing] = React.useState(false);
    const [updatingLink, setUpdatingLink] = React.useState(false);
    const flowOperationPending = isWhatsappApplicationFlowOperationPending({
        saving,
        publishing,
        updatingLink
    });
    const [helpOpen, setHelpOpen] = React.useState(false);
    const [editorState, setEditorState] = React.useState<
        WhatsappApplicationEditorState
    >(() => {
        const initialFlow = buildDefaultFlow();
        return {
            flow: initialFlow,
            optionsDraftByEditorKey: buildWhatsappApplicationOptionsDraftByEditorKey(
                initialFlow.nodes
            )
        };
    });
    const { flow, optionsDraftByEditorKey } = editorState;
    const replaceFlowFromExternal = React.useCallback(
        (nextFlowOrUpdater: WhatsappApplicationFlowUpdate) => {
            setEditorState((current) => {
                const nextFlow =
                    typeof nextFlowOrUpdater === "function"
                        ? nextFlowOrUpdater(current.flow)
                        : nextFlowOrUpdater;
                return {
                    flow: nextFlow,
                    optionsDraftByEditorKey: buildWhatsappApplicationOptionsDraftByEditorKey(
                        nextFlow.nodes
                    )
                };
            });
        },
        []
    );
    const getWhatsappPreRegistrationFieldProps = ({
        field,
        value,
        answerKey,
        draftValue
    }: {
        field:
            | "nodeId"
            | "nodeReference"
            | "answerKey"
            | "options"
            | "conditionValue";
        value: string | string[];
        answerKey?: string;
        draftValue?: string;
    }) => {
        const presentation = getWhatsappPreRegistrationFieldPresentation({
            scope: flow.scope,
            field,
            value,
            answerKey
        });

        return {
            value: presentation
                ? presentation.translationKeys.map((key) => t(key)).join(", ")
                : draftValue !== undefined
                ? draftValue
                : Array.isArray(value)
                ? value.join(", ")
                : value,
            disabled: readonly || presentation?.readonly === true
        };
    };
    const [models, setModels] = React.useState<WhatsappApplicationFlow[]>([]);
    const selectableModels = React.useMemo(
        () => getSelectableWhatsappApplicationModels(models),
        [models]
    );
    const [link, setLink] = React.useState<Compleo.IObject | null>(null);
    const messageTemplateVariables = React.useMemo(
        () =>
            flow.scope === "PRE_REGISTRATION"
                ? [
                      {
                          label: t("whatsappPreRegistrationVariableStoreName"),
                          value: "{{storeName}}"
                      },
                      {
                          label: t("whatsappApplicationVariableCompanyName"),
                          value: "{{companyName}}"
                      }
                  ]
                : [
                      {
                          label: t("whatsappApplicationVariableJobTitle"),
                          value: "{{jobTitle}}"
                      },
                      {
                          label: t("whatsappApplicationVariableCompanyName"),
                          value: "{{companyName}}"
                      }
                  ],
        [flow.scope, t]
    );

    React.useEffect(() => {
        showMessageRef.current = showMessage;
    }, [showMessage]);

    const notify = React.useCallback(
        (
            message: string,
            typeMessage: "success" | "error" | "warning",
            error?: any
        ) => {
            showMessageRef.current({
                message,
                typeMessage,
                timeout: getWhatsappApplicationMessageTimeout(
                    typeMessage,
                    error
                ),
                snackBarOrign: {
                    vertical: "top",
                    horizontal: "center"
                }
            });
        },
        []
    );

    const refreshData = React.useCallback(async () => {
        const requestId = loadRequestRef.current + 1;
        loadRequestRef.current = requestId;

        if (
            !enabled ||
            !companyId ||
            (configurationScope === "job" && !jobId)
        ) {
            setLink(null);
            replaceFlowFromExternal(buildDefaultFlow());
            setLoading(false);
            return;
        }

        setLoading(true);
        try {
            if (configurationScope === "model") {
                if (!modelId) {
                    setLink(null);
                    setModels([]);
                    replaceFlowFromExternal(buildDefaultFlow());
                    setLoading(false);
                    return;
                }
                const modelResponse = await apiDirectCall(
                    "/whatsapp/applicationflowmodel",
                    "post",
                    { companyId, modelId }
                );
                if (loadRequestRef.current !== requestId) {
                    return;
                }
                const modelData = getWhatsappApiResponseData(modelResponse)
                    .model;
                setLink(null);
                setModels([]);
                replaceFlowFromExternal(
                    modelData
                        ? fillWhatsappApplicationMessageTexts(
                              normalizeWhatsappApplicationFlow(modelData),
                              defaultFlowTexts
                          )
                        : buildDefaultFlow()
                );
                return;
            }
            const linkResponse = await apiDirectCall(
                "/whatsapp/jobapplicationlink",
                "post",
                { companyId, jobId }
            );
            const linkData = getWhatsappApiResponseData(linkResponse).link;
            if (loadRequestRef.current !== requestId) {
                return;
            }
            setLink(linkData || null);
            try {
                const modelsResponse = await apiDirectCall(
                    "/whatsapp/applicationflowmodels",
                    "post",
                    { companyId, onlyPublished: true, scope: "JOB" }
                );
                const modelItems =
                    getWhatsappApiResponseData(modelsResponse).models || [];
                if (loadRequestRef.current !== requestId) {
                    return;
                }
                setModels(
                    modelItems.map((item: Compleo.IObject) =>
                        fillWhatsappApplicationMessageTexts(
                            normalizeWhatsappApplicationFlow(item),
                            defaultFlowTexts
                        )
                    )
                );
            } catch {
                if (loadRequestRef.current !== requestId) {
                    return;
                }
                setModels([]);
            }

            if (linkData?.flowId) {
                const flowResponse = await apiDirectCall(
                    "/whatsapp/applicationflow",
                    "post",
                    { companyId, flowId: linkData.flowId }
                );
                const flowData = getWhatsappApiResponseData(flowResponse).flow;
                if (loadRequestRef.current !== requestId) {
                    return;
                }
                const loadedFlow = flowData
                    ? fillWhatsappApplicationMessageTexts(
                          normalizeWhatsappApplicationFlow(flowData),
                          defaultFlowTexts
                      )
                    : buildDefaultFlow();
                const linkHasQualifiedStage = Object.prototype.hasOwnProperty.call(
                    linkData,
                    "qualifiedStageId"
                );
                const linkHasNotQualifiedStage = Object.prototype.hasOwnProperty.call(
                    linkData,
                    "notQualifiedStageId"
                );
                const linkHasNotQualifiedReason = Object.prototype.hasOwnProperty.call(
                    linkData,
                    "notQualifiedDisqualifyReason"
                );
                replaceFlowFromExternal(
                    applyWhatsappApplicationLinkSettings(
                        {
                            ...loadedFlow,
                            qualifiedStageId: linkHasQualifiedStage
                                ? String(linkData.qualifiedStageId || "")
                                : "",
                            notQualifiedStageId: linkHasNotQualifiedStage
                                ? String(linkData.notQualifiedStageId || "")
                                : "",
                            notQualifiedDisqualifyReason: linkHasNotQualifiedReason
                                ? linkData.notQualifiedDisqualifyReason || null
                                : null
                        },
                        linkData
                    )
                );
            } else {
                replaceFlowFromExternal(buildDefaultFlow());
            }
        } catch (error) {
            if (loadRequestRef.current !== requestId) {
                return;
            }
            notify(
                resolveWhatsappApplicationErrorMessage(
                    error,
                    t,
                    t("whatsappApplicationLoadError")
                ),
                "error",
                error
            );
        } finally {
            if (loadRequestRef.current === requestId) {
                setLoading(false);
            }
        }
    }, [
        companyId,
        buildDefaultFlow,
        configurationScope,
        defaultFlowTexts,
        enabled,
        jobId,
        jobTitle,
        modelId,
        notify,
        replaceFlowFromExternal,
        t
    ]);

    const selectExistingFlow = async (selectedModelId: string) => {
        const requestId = loadRequestRef.current + 1;
        loadRequestRef.current = requestId;
        if (!selectedModelId) {
            replaceFlowFromExternal(buildDefaultFlow());
            setSaving(false);
            return;
        }
        const selectedModel = models.find(
            (item) => item.modelId === selectedModelId
        );
        if (!selectedModel) {
            setSaving(false);
            return;
        }
        setSaving(true);
        try {
            const response = await apiDirectCall(
                "/whatsapp/createapplicationflowfrommodel",
                "post",
                {
                    companyId,
                    jobId,
                    modelId: selectedModelId
                }
            );
            if (loadRequestRef.current !== requestId) {
                return;
            }
            const copiedFlow = normalizeWhatsappApplicationFlow(
                getWhatsappApiResponseData(response).flow
            );
            replaceFlowFromExternal((currentFlow) =>
                applyWhatsappApplicationModelFlow({
                    currentFlow,
                    copiedFlow: fillWhatsappApplicationMessageTexts(
                        copiedFlow,
                        defaultFlowTexts
                    )
                })
            );
        } catch (error) {
            if (loadRequestRef.current !== requestId) {
                return;
            }
            notify(
                resolveWhatsappApplicationErrorMessage(
                    error,
                    t,
                    t("whatsappApplicationSaveError")
                ),
                "error",
                error
            );
        } finally {
            if (loadRequestRef.current === requestId) {
                setSaving(false);
            }
        }
    };

    React.useEffect(() => {
        refreshData();
        return () => {
            loadRequestRef.current += 1;
        };
    }, [refreshData, refreshKey]);

    if (!enabled) {
        return null;
    }

    const updateFlow = (
        payloadOrUpdater:
            | Partial<WhatsappApplicationFlow>
            | ((
                  currentFlow: WhatsappApplicationFlow
              ) => WhatsappApplicationFlow)
    ) => {
        editorRevisionRef.current += 1;
        setEditorState((current) => ({
            ...current,
            flow:
                typeof payloadOrUpdater === "function"
                    ? payloadOrUpdater(current.flow)
                    : { ...current.flow, ...payloadOrUpdater }
        }));
    };

    const updateNode = (
        nodeEditorKey: string,
        payload: Partial<Omit<WhatsappApplicationNode, "options">>
    ) => {
        updateFlow((currentFlow) => ({
            ...currentFlow,
            nodes: currentFlow.nodes.map((node) =>
                node.editorKey === nodeEditorKey
                    ? { ...node, ...payload }
                    : node
            )
        }));
    };

    const updateOptionsDraft = (nodeEditorKey: string, text: string) => {
        editorRevisionRef.current += 1;
        setEditorState((current) =>
            updateWhatsappApplicationOptionsDraft(current, nodeEditorKey, text)
        );
    };

    const canonicalizeOptionsDraft = (nodeEditorKey: string) => {
        setEditorState((current) =>
            canonicalizeWhatsappApplicationOptionsDraft(current, nodeEditorKey)
        );
    };

    const updateCondition = (
        nodeEditorKey: string,
        conditionIndex: number,
        payload: Partial<WhatsappApplicationCondition>
    ) => {
        updateFlow((currentFlow) => ({
            ...currentFlow,
            nodes: currentFlow.nodes.map((node) =>
                node.editorKey === nodeEditorKey
                    ? {
                          ...node,
                          conditions: node.conditions.map((condition, index) =>
                              index === conditionIndex
                                  ? { ...condition, ...payload }
                                  : condition
                          )
                      }
                    : node
            )
        }));
    };

    const addNode = () => {
        editorRevisionRef.current += 1;
        const type: WhatsappApplicationNodeType = "question";
        const editorKey = createWhatsappApplicationNodeEditorKey();
        setEditorState((current) => {
            const node: WhatsappApplicationNode = {
                editorKey,
                id: getNextWhatsappApplicationNodeId(current.flow.nodes, type),
                type,
                text: "",
                nextNodeId: "",
                answerKey: "",
                outcome: "",
                options: [],
                conditions: []
            };
            return {
                flow: {
                    ...current.flow,
                    nodes: [...current.flow.nodes, node]
                },
                optionsDraftByEditorKey: {
                    ...current.optionsDraftByEditorKey,
                    [editorKey]: formatWhatsappApplicationOptions(node.options)
                }
            };
        });
    };

    const removeNode = (nodeEditorKey: string) => {
        editorRevisionRef.current += 1;
        setEditorState((current) =>
            removeWhatsappApplicationOptionsNode(current, nodeEditorKey)
        );
    };

    const addCondition = (nodeEditorKey: string) => {
        updateFlow((currentFlow) => ({
            ...currentFlow,
            nodes: currentFlow.nodes.map((node) =>
                node.editorKey === nodeEditorKey
                    ? {
                          ...node,
                          conditions: [
                              ...node.conditions,
                              {
                                  operator: "EQUALS",
                                  value: "",
                                  nextNodeId: "",
                                  scoreDelta: undefined,
                                  eliminatory: false
                              }
                          ]
                      }
                    : node
            )
        }));
    };

    const removeCondition = (nodeEditorKey: string, conditionIndex: number) => {
        updateFlow((currentFlow) => ({
            ...currentFlow,
            nodes: currentFlow.nodes.map((node) =>
                node.editorKey === nodeEditorKey
                    ? {
                          ...node,
                          conditions: node.conditions.filter(
                              (_, index) => index !== conditionIndex
                          )
                      }
                    : node
            )
        }));
    };

    const onNodeDragEnd = (result: DropResult) => {
        const { destination, source } = result;
        if (
            readonly ||
            !destination ||
            destination.droppableId !==
                WHATSAPP_APPLICATION_NODES_DROPPABLE_ID ||
            source.droppableId !== WHATSAPP_APPLICATION_NODES_DROPPABLE_ID ||
            destination.index === source.index
        ) {
            return;
        }

        updateFlow((currentFlow) =>
            reorderWhatsappApplicationFlowNodes(
                currentFlow,
                source.index,
                destination.index
            )
        );
    };

    const saveJobLinkFlow = async (
        savedFlow: WhatsappApplicationFlow,
        requestId: number,
        active = link?.active === true
    ) => {
        if (!savedFlow.flowId) {
            return;
        }

        const response = await apiDirectCall(
            "/whatsapp/upsertjobapplicationlink",
            "post",
            {
                companyId,
                jobId,
                active,
                jobTitle,
                flowId: savedFlow.flowId,
                flowVersion: savedFlow.publishedVersion || 0,
                requireApplicationFormRequiredFields:
                    savedFlow.requireApplicationFormRequiredFields,
                collectScreeningQuestionnaire:
                    savedFlow.collectScreeningQuestionnaire,
                qualifiedStageId: savedFlow.qualifiedStageId,
                notQualifiedStageId: savedFlow.notQualifiedStageId,
                notQualifiedDisqualifyReason:
                    savedFlow.notQualifiedDisqualifyReason
            }
        );
        if (loadRequestRef.current !== requestId) {
            return;
        }
        setLink(getWhatsappApiResponseData(response).link || null);
    };

    const saveFlow = async ({
        requestId,
        expectedEditorRevision,
        showSuccess = true,
        syncJobLink = true
    }: {
        requestId?: number;
        expectedEditorRevision?: number;
        showSuccess?: boolean;
        syncJobLink?: boolean;
    } = {}) => {
        if (!enabled || updatingLink || readonly) {
            return null;
        }
        const editorRevision =
            expectedEditorRevision ?? editorRevisionRef.current;
        if (editorRevisionRef.current !== editorRevision) {
            return null;
        }
        const currentRequestId =
            requestId !== undefined ? requestId : loadRequestRef.current + 1;
        loadRequestRef.current = currentRequestId;
        setSaving(true);
        try {
            const flowForSave = serializeWhatsappApplicationFlowForSave(flow);
            const response = await apiDirectCall(
                configurationScope === "model"
                    ? "/whatsapp/saveapplicationflowmodel"
                    : "/whatsapp/saveapplicationflow",
                "post",
                {
                    companyId,
                    scope: flowForSave.scope,
                    modelId: getWhatsappApplicationModelIdForSave({
                        configurationScope,
                        routeModelId: modelId,
                        flowModelId: flowForSave.modelId
                    }),
                    flowId: flowForSave.flowId,
                    jobId: configurationScope === "job" ? jobId : undefined,
                    name: flowForSave.name,
                    sourceModelId: flowForSave.sourceModelId,
                    sourceModelVersion: flowForSave.sourceModelVersion,
                    requireApplicationFormRequiredFields:
                        flowForSave.requireApplicationFormRequiredFields,
                    collectScreeningQuestionnaire:
                        flowForSave.collectScreeningQuestionnaire,
                    applicationStartText: flowForSave.applicationStartText,
                    existingCandidateFoundText:
                        flowForSave.existingCandidateFoundText,
                    existingCandidateInvalidChoiceText:
                        flowForSave.existingCandidateInvalidChoiceText,
                    consentInvalidAnswerText:
                        flowForSave.consentInvalidAnswerText,
                    resumeInvalidAnswerText:
                        flowForSave.resumeInvalidAnswerText,
                    cvParsingStartedText: flowForSave.cvParsingStartedText,
                    cvParsingCompletedText: flowForSave.cvParsingCompletedText,
                    nodes: flowForSave.nodes
                }
            );
            const savedFlow =
                getWhatsappApiResponseData(response).flow ||
                getWhatsappApiResponseData(response).model;
            const normalizedFlow = {
                ...normalizeWhatsappApplicationFlow(savedFlow),
                qualifiedStageId: flow.qualifiedStageId,
                notQualifiedStageId: flow.notQualifiedStageId,
                notQualifiedDisqualifyReason: flow.notQualifiedDisqualifyReason
            };
            if (
                loadRequestRef.current !== currentRequestId ||
                editorRevisionRef.current !== editorRevision
            ) {
                return null;
            }
            replaceFlowFromExternal(normalizedFlow);
            if (configurationScope === "job" && syncJobLink) {
                await saveJobLinkFlow(normalizedFlow, currentRequestId);
            }
            if (
                loadRequestRef.current !== currentRequestId ||
                editorRevisionRef.current !== editorRevision
            ) {
                return null;
            }
            if (showSuccess) {
                notify(t("whatsappApplicationSaveSuccess"), "success");
                if (configurationScope === "model") {
                    onModelSaved?.(normalizedFlow);
                }
                onUpdated?.();
            }
            return normalizedFlow;
        } catch (error) {
            if (
                loadRequestRef.current !== currentRequestId ||
                editorRevisionRef.current !== editorRevision
            ) {
                return null;
            }
            notify(
                resolveWhatsappApplicationErrorMessage(
                    error,
                    t,
                    t("whatsappApplicationSaveError")
                ),
                "error",
                error
            );
            return null;
        } finally {
            setSaving(false);
        }
    };

    const publishFlow = async () => {
        if (!enabled || updatingLink || readonly) {
            return;
        }
        const editorRevision = editorRevisionRef.current;
        const validation = canPublishWhatsappApplicationFlow(flow);
        if (!validation.canPublish) {
            notify(
                t(`whatsappApplicationPublish_${validation.reason}`),
                "warning"
            );
            return;
        }

        const requestId = loadRequestRef.current + 1;
        loadRequestRef.current = requestId;
        setPublishing(true);
        let savedModelFlow: WhatsappApplicationFlow | null = null;
        const openSavedModel = () => {
            if (configurationScope === "model" && savedModelFlow?.modelId) {
                onModelSaved?.(savedModelFlow);
            }
        };
        try {
            const publishedFlow = await publishWhatsappApplicationFlowDraft({
                saveDraft: async () => {
                    const savedFlow = await saveFlow({
                        requestId,
                        expectedEditorRevision: editorRevision,
                        showSuccess: false,
                        syncJobLink: false
                    });
                    if (
                        (configurationScope === "job" && !savedFlow?.flowId) ||
                        (configurationScope === "model" &&
                            !savedFlow?.modelId) ||
                        loadRequestRef.current !== requestId ||
                        editorRevisionRef.current !== editorRevision
                    ) {
                        return null;
                    }
                    if (configurationScope === "model") {
                        savedModelFlow = savedFlow;
                    }
                    return savedFlow;
                },
                publishDraft: async (savedFlow) => {
                    if (
                        loadRequestRef.current !== requestId ||
                        editorRevisionRef.current !== editorRevision
                    ) {
                        return null;
                    }
                    const response = await apiDirectCall(
                        configurationScope === "model"
                            ? "/whatsapp/publishapplicationflowmodel"
                            : "/whatsapp/publishapplicationflow",
                        "post",
                        {
                            companyId,
                            flowId: savedFlow.flowId,
                            modelId: savedFlow.modelId
                        }
                    );
                    if (
                        loadRequestRef.current !== requestId ||
                        editorRevisionRef.current !== editorRevision
                    ) {
                        return null;
                    }
                    const version = getWhatsappApiResponseData(response)
                        .version;
                    return Number(version?.versionNumber || 0);
                },
                acceptPublishedFlow: (acceptedFlow) => {
                    setEditorState((current) => ({
                        ...current,
                        flow: {
                            ...current.flow,
                            flowId: acceptedFlow.flowId,
                            modelId: acceptedFlow.modelId,
                            publishedVersion: acceptedFlow.publishedVersion
                        }
                    }));
                },
                syncJobLink:
                    configurationScope === "job"
                        ? (acceptedFlow) =>
                              saveJobLinkFlow(acceptedFlow, requestId)
                        : undefined
            });
            if (!publishedFlow) {
                if (
                    loadRequestRef.current !== requestId ||
                    editorRevisionRef.current !== editorRevision
                ) {
                    return;
                }
                openSavedModel();
                return;
            }
            if (
                loadRequestRef.current !== requestId ||
                editorRevisionRef.current !== editorRevision
            ) {
                return;
            }
            if (configurationScope === "model") {
                onModelSaved?.(publishedFlow);
            }
            onUpdated?.();
            notify(t("whatsappApplicationPublishSuccess"), "success");
        } catch (error) {
            if (
                loadRequestRef.current !== requestId ||
                editorRevisionRef.current !== editorRevision
            ) {
                return;
            }
            notify(
                resolveWhatsappApplicationErrorMessage(
                    error,
                    t,
                    t("whatsappApplicationPublishError")
                ),
                "error",
                error
            );
            openSavedModel();
        } finally {
            setPublishing(false);
        }
    };

    const updateLinkActive = async (active: boolean) => {
        if (!enabled || readonly) {
            return;
        }
        const requestId = loadRequestRef.current + 1;
        loadRequestRef.current = requestId;
        const linkState = toggleWhatsappApplicationLinkActive({
            active,
            flowId: flow.flowId,
            publishedVersion: flow.publishedVersion
        });

        if (!linkState.canActivate) {
            notify(t(`whatsappApplicationLink_${linkState.reason}`), "warning");
            return;
        }

        setUpdatingLink(true);
        try {
            const response = await apiDirectCall(
                "/whatsapp/upsertjobapplicationlink",
                "post",
                {
                    companyId,
                    jobId,
                    active,
                    flowId: flow.flowId || link?.flowId || "",
                    flowVersion: linkState.flowVersion,
                    qualifiedStageId: flow.qualifiedStageId,
                    notQualifiedStageId: flow.notQualifiedStageId,
                    notQualifiedDisqualifyReason:
                        flow.notQualifiedDisqualifyReason,
                    requireApplicationFormRequiredFields:
                        flow.requireApplicationFormRequiredFields,
                    collectScreeningQuestionnaire:
                        flow.collectScreeningQuestionnaire
                }
            );
            if (loadRequestRef.current !== requestId) {
                return;
            }
            setLink(getWhatsappApiResponseData(response).link || null);
            onUpdated?.();
            notify(t("whatsappApplicationLinkSaveSuccess"), "success");
        } catch (error) {
            if (loadRequestRef.current !== requestId) {
                return;
            }
            notify(
                resolveWhatsappApplicationErrorMessage(
                    error,
                    t,
                    t("whatsappApplicationLinkSaveError")
                ),
                "error",
                error
            );
        } finally {
            setUpdatingLink(false);
        }
    };

    const copyLink = async () => {
        if (!link?.waMeUrl) {
            return;
        }

        const requestId = loadRequestRef.current;
        try {
            await navigator.clipboard.writeText(String(link.waMeUrl));
            if (loadRequestRef.current !== requestId) {
                return;
            }
            notify(t("copyJobLinkSucess"), "success");
        } catch (error) {
            if (loadRequestRef.current !== requestId) {
                return;
            }
            notify(t("whatsappApplicationCopyError"), "error", error);
        }
    };

    if (!enabled) {
        return (
            <Paper elevation={1} className={classes?.paper}>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <Box display="flex" alignItems="center">
                            <WhatsAppIcon style={{ marginRight: 8 }} />
                            <Typography variant="subtitle1">
                                {t("whatsappApplicationTitle")}
                            </Typography>
                        </Box>
                    </Grid>
                    <Grid item xs={12}>
                        <Alert severity="info">
                            {t("whatsappApplicationDisabled")}
                        </Alert>
                    </Grid>
                </Grid>
            </Paper>
        );
    }

    if (mode === "summary" && loading) {
        return (
            <Paper elevation={1} className={classes?.paper}>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <Box display="flex" alignItems="center">
                            <WhatsAppIcon style={{ marginRight: 8 }} />
                            <Typography variant="subtitle1">
                                {t("whatsappApplicationTitle")}
                            </Typography>
                        </Box>
                    </Grid>
                    <Grid item xs={12}>
                        <Typography variant="body2">
                            {t("whatsappApplicationLoading")}
                        </Typography>
                    </Grid>
                </Grid>
            </Paper>
        );
    }

    if (mode === "summary") {
        return (
            <WhatsappApplicationSummary
                t={t}
                flow={flow}
                link={link}
                qualifiedStageOptions={qualifiedStageOptions}
                notQualifiedStageOptions={notQualifiedStageOptions}
                onEdit={readonly ? undefined : onEdit}
                onCopyLink={copyLink}
            />
        );
    }

    return (
        <>
            <Paper elevation={1} className={classes?.paper}>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <Box display="flex" alignItems="center">
                            <WhatsAppIcon style={{ marginRight: 8 }} />
                            <Typography variant="subtitle1">
                                {t("whatsappApplicationTitle")}
                            </Typography>
                            <Tooltip title={t("whatsappApplicationHelpOpen")}>
                                <IconButton
                                    aria-label={t(
                                        "whatsappApplicationHelpOpen"
                                    )}
                                    onClick={() => setHelpOpen(true)}
                                    size="small"
                                >
                                    <HelpOutlineIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                            {link?.active === true && (
                                <Box marginLeft={1}>
                                    <Chip
                                        size="small"
                                        color="primary"
                                        label={t(
                                            "whatsappApplicationLinkActive"
                                        )}
                                    />
                                </Box>
                            )}
                        </Box>
                    </Grid>
                    {loading ? (
                        <Grid item xs={12}>
                            <Typography variant="body2">
                                {t("whatsappApplicationLoading")}
                            </Typography>
                        </Grid>
                    ) : (
                        <>
                            <Grid item xs={12} md={8}>
                                <TextField
                                    fullWidth
                                    label={t("whatsappApplicationFlowName")}
                                    value={flow.name}
                                    onChange={(event) =>
                                        updateFlow({ name: event.target.value })
                                    }
                                    variant="outlined"
                                    size="small"
                                    inputProps={{
                                        maxLength: WHATSAPP_APPLICATION_INTERACTIVE_MESSAGE_TEXT_MAX_LENGTH
                                    }}
                                    disabled={readonly}
                                />
                            </Grid>
                            {configurationScope === "job" && (
                                <Grid item xs={12} md={4}>
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={link?.active === true}
                                                disabled={
                                                    flowOperationPending ||
                                                    loading ||
                                                    readonly
                                                }
                                                onChange={(event) =>
                                                    updateLinkActive(
                                                        event.target.checked
                                                    )
                                                }
                                                color="primary"
                                            />
                                        }
                                        label={t(
                                            "whatsappApplicationLinkEnabled"
                                        )}
                                    />
                                </Grid>
                            )}
                            {configurationScope === "job" && link?.waMeUrl && (
                                <Grid item xs={12}>
                                    <Alert>
                                        <a
                                            href={String(link.waMeUrl)}
                                            target="_blank"
                                            rel="noreferrer"
                                            style={{ marginRight: 12 }}
                                        >
                                            {link.waMeUrl}
                                        </a>
                                        <Tooltip title={t("copyJobLink")}>
                                            <IconButton
                                                onClick={copyLink}
                                                size="small"
                                            >
                                                <FileCopyIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </Alert>
                                </Grid>
                            )}
                            {configurationScope === "job" && (
                                <>
                                    <Grid item xs={12}>
                                        <Divider />
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            select
                                            fullWidth
                                            label={t(
                                                "whatsappApplicationExistingFlow"
                                            )}
                                            value={flow.sourceModelId || ""}
                                            onChange={(event) =>
                                                selectExistingFlow(
                                                    event.target.value
                                                )
                                            }
                                            variant="outlined"
                                            size="small"
                                            disabled={
                                                flowOperationPending || readonly
                                            }
                                            InputLabelProps={{ shrink: true }}
                                            SelectProps={{ displayEmpty: true }}
                                        >
                                            <MenuItem value="">
                                                {t(
                                                    "whatsappApplicationNewFlow"
                                                )}
                                            </MenuItem>
                                            {selectableModels.map(
                                                (flowItem) => (
                                                    <MenuItem
                                                        key={flowItem.modelId}
                                                        value={flowItem.modelId}
                                                    >
                                                        {flowItem.name ||
                                                            flowItem.modelId}
                                                    </MenuItem>
                                                )
                                            )}
                                        </TextField>
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={
                                                        flow.requireApplicationFormRequiredFields
                                                    }
                                                    onChange={(event) =>
                                                        updateFlow({
                                                            requireApplicationFormRequiredFields:
                                                                event.target
                                                                    .checked
                                                        })
                                                    }
                                                    color="primary"
                                                    disabled={readonly}
                                                />
                                            }
                                            label={t(
                                                "whatsappApplicationRequireRequiredFields"
                                            )}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={
                                                        flow.collectScreeningQuestionnaire
                                                    }
                                                    onChange={(event) =>
                                                        updateFlow({
                                                            collectScreeningQuestionnaire:
                                                                event.target
                                                                    .checked
                                                        })
                                                    }
                                                    color="primary"
                                                    disabled={readonly}
                                                />
                                            }
                                            label={t(
                                                "whatsappApplicationCollectScreeningQuestionnaire"
                                            )}
                                        />
                                    </Grid>
                                </>
                            )}
                            {configurationScope === "job" && (
                                <Grid item xs={12} md={3}>
                                    <TextField
                                        select
                                        fullWidth
                                        label={t(
                                            "whatsappApplicationQualifiedStageId"
                                        )}
                                        value={flow.qualifiedStageId}
                                        onChange={(event) =>
                                            updateFlow({
                                                qualifiedStageId:
                                                    event.target.value
                                            })
                                        }
                                        variant="outlined"
                                        size="small"
                                        disabled={readonly}
                                        InputLabelProps={{ shrink: true }}
                                        SelectProps={{ displayEmpty: true }}
                                    >
                                        <MenuItem value="">
                                            {t("whatsappApplicationNoStage")}
                                        </MenuItem>
                                        {qualifiedStageOptions.map((stage) => (
                                            <MenuItem
                                                key={stage.value}
                                                value={stage.value}
                                            >
                                                {stage.label}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                </Grid>
                            )}
                            {configurationScope === "job" && (
                                <Grid item xs={12} md={3}>
                                    <TextField
                                        select
                                        fullWidth
                                        label={t(
                                            "whatsappApplicationNotQualifiedStageId"
                                        )}
                                        value={flow.notQualifiedStageId}
                                        onChange={(event) =>
                                            updateFlow({
                                                notQualifiedStageId:
                                                    event.target.value
                                            })
                                        }
                                        variant="outlined"
                                        size="small"
                                        disabled={readonly}
                                        InputLabelProps={{ shrink: true }}
                                        SelectProps={{ displayEmpty: true }}
                                    >
                                        <MenuItem value="">
                                            {t("whatsappApplicationNoStage")}
                                        </MenuItem>
                                        {notQualifiedStageOptions.map(
                                            (stage) => (
                                                <MenuItem
                                                    key={stage.value}
                                                    value={stage.value}
                                                >
                                                    {stage.label}
                                                </MenuItem>
                                            )
                                        )}
                                    </TextField>
                                </Grid>
                            )}
                            {configurationScope === "job" && (
                                <Grid item xs={12} md={3}>
                                    <TextField
                                        fullWidth
                                        label={t(
                                            "whatsappApplicationNotQualifiedReason"
                                        )}
                                        value={
                                            flow.notQualifiedDisqualifyReason
                                                ?.label || ""
                                        }
                                        onChange={(event) =>
                                            updateFlow({
                                                notQualifiedDisqualifyReason: event
                                                    .target.value
                                                    ? {
                                                          label:
                                                              event.target
                                                                  .value,
                                                          value:
                                                              event.target.value
                                                      }
                                                    : null
                                            })
                                        }
                                        variant="outlined"
                                        size="small"
                                        disabled={readonly}
                                    />
                                </Grid>
                            )}
                            <Grid item xs={12}>
                                <Divider />
                            </Grid>
                            <Grid item xs={12}>
                                <Typography variant="subtitle2">
                                    {t("whatsappApplicationAutomaticMessages")}
                                </Typography>
                            </Grid>
                            <Grid item xs={12}>
                                <Box display="flex" alignItems="center">
                                    <Typography
                                        variant="body2"
                                        color="textSecondary"
                                        style={{ marginRight: 8 }}
                                    >
                                        {t(
                                            "whatsappApplicationAvailableVariables"
                                        )}
                                    </Typography>
                                    {messageTemplateVariables.map(
                                        (variable) => (
                                            <Box
                                                key={variable.value}
                                                marginRight={1}
                                            >
                                                <Chip
                                                    size="small"
                                                    label={`${variable.label}: ${variable.value}`}
                                                />
                                            </Box>
                                        )
                                    )}
                                </Box>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    multiline
                                    minRows={4}
                                    label={t(
                                        "whatsappApplicationApplicationStartText"
                                    )}
                                    helperText={t(
                                        flow.scope === "PRE_REGISTRATION"
                                            ? "whatsappPreRegistrationApplicationStartTextHelp"
                                            : "whatsappApplicationApplicationStartTextHelp",
                                        flow.scope === "PRE_REGISTRATION"
                                            ? whatsappPreRegistrationTemplatePlaceholderValues
                                            : undefined
                                    )}
                                    value={flow.applicationStartText}
                                    onChange={(event) =>
                                        updateFlow({
                                            applicationStartText:
                                                event.target.value
                                        })
                                    }
                                    variant="outlined"
                                    size="small"
                                    inputProps={{
                                        maxLength: WHATSAPP_APPLICATION_INTERACTIVE_MESSAGE_TEXT_MAX_LENGTH
                                    }}
                                    disabled={readonly}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    multiline
                                    minRows={4}
                                    label={t(
                                        "whatsappApplicationExistingCandidateFoundText"
                                    )}
                                    helperText={t(
                                        "whatsappApplicationExistingCandidateFoundTextHelp"
                                    )}
                                    value={flow.existingCandidateFoundText}
                                    onChange={(event) =>
                                        updateFlow({
                                            existingCandidateFoundText:
                                                event.target.value
                                        })
                                    }
                                    variant="outlined"
                                    size="small"
                                    inputProps={{
                                        maxLength: WHATSAPP_APPLICATION_INTERACTIVE_MESSAGE_TEXT_MAX_LENGTH
                                    }}
                                    disabled={readonly}
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <TextField
                                    fullWidth
                                    multiline
                                    minRows={2}
                                    label={t(
                                        "whatsappApplicationExistingCandidateInvalidChoiceText"
                                    )}
                                    value={
                                        flow.existingCandidateInvalidChoiceText
                                    }
                                    onChange={(event) =>
                                        updateFlow({
                                            existingCandidateInvalidChoiceText:
                                                event.target.value
                                        })
                                    }
                                    variant="outlined"
                                    size="small"
                                    inputProps={{
                                        maxLength: WHATSAPP_APPLICATION_INTERACTIVE_MESSAGE_TEXT_MAX_LENGTH
                                    }}
                                    disabled={readonly}
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <TextField
                                    fullWidth
                                    multiline
                                    minRows={2}
                                    label={t(
                                        "whatsappApplicationConsentInvalidAnswerText"
                                    )}
                                    value={flow.consentInvalidAnswerText}
                                    onChange={(event) =>
                                        updateFlow({
                                            consentInvalidAnswerText:
                                                event.target.value
                                        })
                                    }
                                    variant="outlined"
                                    size="small"
                                    inputProps={{
                                        maxLength: WHATSAPP_APPLICATION_TEXT_MESSAGE_MAX_LENGTH
                                    }}
                                    disabled={readonly}
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <TextField
                                    fullWidth
                                    multiline
                                    minRows={2}
                                    label={t(
                                        "whatsappApplicationResumeInvalidAnswerText"
                                    )}
                                    value={flow.resumeInvalidAnswerText}
                                    onChange={(event) =>
                                        updateFlow({
                                            resumeInvalidAnswerText:
                                                event.target.value
                                        })
                                    }
                                    variant="outlined"
                                    size="small"
                                    inputProps={{
                                        maxLength: WHATSAPP_APPLICATION_TEXT_MESSAGE_MAX_LENGTH
                                    }}
                                    disabled={readonly}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    multiline
                                    minRows={2}
                                    label={t(
                                        "whatsappApplicationCvParsingStartedText"
                                    )}
                                    value={flow.cvParsingStartedText}
                                    onChange={(event) =>
                                        updateFlow({
                                            cvParsingStartedText:
                                                event.target.value
                                        })
                                    }
                                    variant="outlined"
                                    size="small"
                                    inputProps={{
                                        maxLength: WHATSAPP_APPLICATION_TEXT_MESSAGE_MAX_LENGTH
                                    }}
                                    disabled={readonly}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    multiline
                                    minRows={2}
                                    label={t(
                                        "whatsappApplicationCvParsingCompletedText"
                                    )}
                                    value={flow.cvParsingCompletedText}
                                    onChange={(event) =>
                                        updateFlow({
                                            cvParsingCompletedText:
                                                event.target.value
                                        })
                                    }
                                    variant="outlined"
                                    size="small"
                                    inputProps={{
                                        maxLength: WHATSAPP_APPLICATION_TEXT_MESSAGE_MAX_LENGTH
                                    }}
                                    disabled={readonly}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <DragDropContext onDragEnd={onNodeDragEnd}>
                                    <Droppable
                                        droppableId={
                                            WHATSAPP_APPLICATION_NODES_DROPPABLE_ID
                                        }
                                        type="whatsappApplicationNode"
                                    >
                                        {(
                                            droppableProvided: DroppableProvided
                                        ) => (
                                            <Box
                                                ref={droppableProvided.innerRef}
                                                {...droppableProvided.droppableProps}
                                            >
                                                {flow.nodes.map(
                                                    (node, index) => (
                                                        <Draggable
                                                            key={node.editorKey}
                                                            draggableId={
                                                                node.editorKey
                                                            }
                                                            index={index}
                                                            isDragDisabled={
                                                                readonly
                                                            }
                                                        >
                                                            {(
                                                                draggableProvided: DraggableProvided,
                                                                snapshot: DraggableStateSnapshot
                                                            ) => (
                                                                <Box
                                                                    ref={
                                                                        draggableProvided.innerRef
                                                                    }
                                                                    {...draggableProvided.draggableProps}
                                                                    marginBottom={
                                                                        1.5
                                                                    }
                                                                    style={{
                                                                        ...draggableProvided
                                                                            .draggableProps
                                                                            .style
                                                                    }}
                                                                >
                                                                    <Paper
                                                                        variant="outlined"
                                                                        elevation={
                                                                            snapshot.isDragging
                                                                                ? 3
                                                                                : 0
                                                                        }
                                                                    >
                                                                        <Box
                                                                            padding={
                                                                                2
                                                                            }
                                                                        >
                                                                            <Grid
                                                                                container
                                                                                spacing={
                                                                                    2
                                                                                }
                                                                            >
                                                                                <Grid
                                                                                    item
                                                                                    xs={
                                                                                        12
                                                                                    }
                                                                                >
                                                                                    <Box
                                                                                        display="flex"
                                                                                        alignItems="center"
                                                                                    >
                                                                                        <Tooltip
                                                                                            title={t(
                                                                                                "whatsappApplicationMoveNode"
                                                                                            )}
                                                                                        >
                                                                                            <span>
                                                                                                <IconButton
                                                                                                    aria-label={t(
                                                                                                        "whatsappApplicationMoveNode"
                                                                                                    )}
                                                                                                    size="small"
                                                                                                    disabled={
                                                                                                        readonly
                                                                                                    }
                                                                                                    {...(draggableProvided.dragHandleProps ||
                                                                                                        {})}
                                                                                                >
                                                                                                    <DragIndicatorIcon fontSize="small" />
                                                                                                </IconButton>
                                                                                            </span>
                                                                                        </Tooltip>
                                                                                        <Typography
                                                                                            variant="subtitle2"
                                                                                            color="textSecondary"
                                                                                        >
                                                                                            {t(
                                                                                                "whatsappApplicationNodeOrder",
                                                                                                {
                                                                                                    order:
                                                                                                        index +
                                                                                                        1
                                                                                                }
                                                                                            )}
                                                                                        </Typography>
                                                                                    </Box>
                                                                                </Grid>
                                                                                <Grid
                                                                                    item
                                                                                    xs={
                                                                                        12
                                                                                    }
                                                                                    md={
                                                                                        3
                                                                                    }
                                                                                >
                                                                                    <TextField
                                                                                        fullWidth
                                                                                        label={t(
                                                                                            "whatsappApplicationNodeId"
                                                                                        )}
                                                                                        {...getWhatsappPreRegistrationFieldProps(
                                                                                            {
                                                                                                field:
                                                                                                    "nodeId",
                                                                                                value:
                                                                                                    node.id
                                                                                            }
                                                                                        )}
                                                                                        onChange={(
                                                                                            event
                                                                                        ) =>
                                                                                            updateNode(
                                                                                                node.editorKey,
                                                                                                {
                                                                                                    id:
                                                                                                        event
                                                                                                            .target
                                                                                                            .value
                                                                                                }
                                                                                            )
                                                                                        }
                                                                                        variant="outlined"
                                                                                        size="small"
                                                                                    />
                                                                                </Grid>
                                                                                <Grid
                                                                                    item
                                                                                    xs={
                                                                                        12
                                                                                    }
                                                                                    md={
                                                                                        3
                                                                                    }
                                                                                >
                                                                                    <TextField
                                                                                        select
                                                                                        fullWidth
                                                                                        label={t(
                                                                                            "whatsappApplicationNodeType"
                                                                                        )}
                                                                                        value={
                                                                                            node.type
                                                                                        }
                                                                                        onChange={(
                                                                                            event
                                                                                        ) =>
                                                                                            updateNode(
                                                                                                node.editorKey,
                                                                                                {
                                                                                                    type: event
                                                                                                        .target
                                                                                                        .value as WhatsappApplicationNodeType
                                                                                                }
                                                                                            )
                                                                                        }
                                                                                        variant="outlined"
                                                                                        size="small"
                                                                                        disabled={
                                                                                            readonly
                                                                                        }
                                                                                    >
                                                                                        {WHATSAPP_APPLICATION_NODE_TYPES.filter(
                                                                                            (
                                                                                                nodeType
                                                                                            ) =>
                                                                                                flow.scope !==
                                                                                                    "PRE_REGISTRATION" ||
                                                                                                nodeType !==
                                                                                                    "screeningQuestionnaire"
                                                                                        ).map(
                                                                                            (
                                                                                                nodeType
                                                                                            ) => (
                                                                                                <MenuItem
                                                                                                    key={
                                                                                                        nodeType
                                                                                                    }
                                                                                                    value={
                                                                                                        nodeType
                                                                                                    }
                                                                                                    disabled={
                                                                                                        nodeType ===
                                                                                                            "screeningQuestionnaire" &&
                                                                                                        !hasScreeningQuestionnaire
                                                                                                    }
                                                                                                >
                                                                                                    {t(
                                                                                                        getWhatsappApplicationNodeTypeLabelKey(
                                                                                                            nodeType,
                                                                                                            configurationScope,
                                                                                                            hasScreeningQuestionnaire
                                                                                                        )
                                                                                                    )}
                                                                                                </MenuItem>
                                                                                            )
                                                                                        )}
                                                                                    </TextField>
                                                                                </Grid>
                                                                                <Grid
                                                                                    item
                                                                                    xs={
                                                                                        12
                                                                                    }
                                                                                    md={
                                                                                        5
                                                                                    }
                                                                                >
                                                                                    <TextField
                                                                                        fullWidth
                                                                                        label={t(
                                                                                            "whatsappApplicationNextNode"
                                                                                        )}
                                                                                        {...getWhatsappPreRegistrationFieldProps(
                                                                                            {
                                                                                                field:
                                                                                                    "nodeReference",
                                                                                                value:
                                                                                                    node.nextNodeId
                                                                                            }
                                                                                        )}
                                                                                        onChange={(
                                                                                            event
                                                                                        ) =>
                                                                                            updateNode(
                                                                                                node.editorKey,
                                                                                                {
                                                                                                    nextNodeId:
                                                                                                        event
                                                                                                            .target
                                                                                                            .value
                                                                                                }
                                                                                            )
                                                                                        }
                                                                                        variant="outlined"
                                                                                        size="small"
                                                                                    />
                                                                                </Grid>
                                                                                <Grid
                                                                                    item
                                                                                    xs={
                                                                                        12
                                                                                    }
                                                                                    md={
                                                                                        1
                                                                                    }
                                                                                >
                                                                                    <Tooltip
                                                                                        title={t(
                                                                                            "whatsappApplicationRemoveNode"
                                                                                        )}
                                                                                    >
                                                                                        <span>
                                                                                            <IconButton
                                                                                                aria-label={t(
                                                                                                    "whatsappApplicationRemoveNode"
                                                                                                )}
                                                                                                onClick={() =>
                                                                                                    removeNode(
                                                                                                        node.editorKey
                                                                                                    )
                                                                                                }
                                                                                                size="small"
                                                                                                disabled={
                                                                                                    readonly ||
                                                                                                    flow
                                                                                                        .nodes
                                                                                                        .length <=
                                                                                                        1
                                                                                                }
                                                                                            >
                                                                                                <DeleteIcon fontSize="small" />
                                                                                            </IconButton>
                                                                                        </span>
                                                                                    </Tooltip>
                                                                                </Grid>
                                                                                <Grid
                                                                                    item
                                                                                    xs={
                                                                                        12
                                                                                    }
                                                                                >
                                                                                    <TextField
                                                                                        fullWidth
                                                                                        multiline
                                                                                        minRows={
                                                                                            2
                                                                                        }
                                                                                        label={t(
                                                                                            "whatsappApplicationNodeText"
                                                                                        )}
                                                                                        value={
                                                                                            node.text
                                                                                        }
                                                                                        onChange={(
                                                                                            event
                                                                                        ) =>
                                                                                            updateNode(
                                                                                                node.editorKey,
                                                                                                {
                                                                                                    text:
                                                                                                        event
                                                                                                            .target
                                                                                                            .value
                                                                                                }
                                                                                            )
                                                                                        }
                                                                                        variant="outlined"
                                                                                        size="small"
                                                                                        disabled={
                                                                                            readonly
                                                                                        }
                                                                                    />
                                                                                </Grid>
                                                                                {node.type ===
                                                                                    "question" && (
                                                                                    <>
                                                                                        <Grid
                                                                                            item
                                                                                            xs={
                                                                                                12
                                                                                            }
                                                                                            md={
                                                                                                6
                                                                                            }
                                                                                        >
                                                                                            <TextField
                                                                                                fullWidth
                                                                                                label={t(
                                                                                                    "whatsappApplicationAnswerKey"
                                                                                                )}
                                                                                                {...getWhatsappPreRegistrationFieldProps(
                                                                                                    {
                                                                                                        field:
                                                                                                            "answerKey",
                                                                                                        value:
                                                                                                            node.answerKey
                                                                                                    }
                                                                                                )}
                                                                                                onChange={(
                                                                                                    event
                                                                                                ) =>
                                                                                                    updateNode(
                                                                                                        node.editorKey,
                                                                                                        {
                                                                                                            answerKey:
                                                                                                                event
                                                                                                                    .target
                                                                                                                    .value
                                                                                                        }
                                                                                                    )
                                                                                                }
                                                                                                variant="outlined"
                                                                                                size="small"
                                                                                            />
                                                                                        </Grid>
                                                                                        <Grid
                                                                                            item
                                                                                            xs={
                                                                                                12
                                                                                            }
                                                                                            md={
                                                                                                6
                                                                                            }
                                                                                        >
                                                                                            <TextField
                                                                                                fullWidth
                                                                                                label={t(
                                                                                                    "whatsappApplicationOptions"
                                                                                                )}
                                                                                                {...getWhatsappPreRegistrationFieldProps(
                                                                                                    {
                                                                                                        field:
                                                                                                            "options",
                                                                                                        value:
                                                                                                            node.options,
                                                                                                        answerKey:
                                                                                                            node.answerKey,
                                                                                                        draftValue:
                                                                                                            optionsDraftByEditorKey[
                                                                                                                node
                                                                                                                    .editorKey
                                                                                                            ] ??
                                                                                                            formatWhatsappApplicationOptions(
                                                                                                                node.options
                                                                                                            )
                                                                                                    }
                                                                                                )}
                                                                                                onChange={(
                                                                                                    event
                                                                                                ) =>
                                                                                                    updateOptionsDraft(
                                                                                                        node.editorKey,
                                                                                                        event
                                                                                                            .target
                                                                                                            .value
                                                                                                    )
                                                                                                }
                                                                                                onBlur={() =>
                                                                                                    canonicalizeOptionsDraft(
                                                                                                        node.editorKey
                                                                                                    )
                                                                                                }
                                                                                                variant="outlined"
                                                                                                size="small"
                                                                                            />
                                                                                        </Grid>
                                                                                    </>
                                                                                )}
                                                                                {node.type ===
                                                                                    "end" && (
                                                                                    <Grid
                                                                                        item
                                                                                        xs={
                                                                                            12
                                                                                        }
                                                                                        md={
                                                                                            6
                                                                                        }
                                                                                    >
                                                                                        <TextField
                                                                                            select
                                                                                            fullWidth
                                                                                            label={t(
                                                                                                "whatsappApplicationOutcome"
                                                                                            )}
                                                                                            value={
                                                                                                node.outcome
                                                                                            }
                                                                                            onChange={(
                                                                                                event
                                                                                            ) =>
                                                                                                updateNode(
                                                                                                    node.editorKey,
                                                                                                    {
                                                                                                        outcome: event
                                                                                                            .target
                                                                                                            .value as any
                                                                                                    }
                                                                                                )
                                                                                            }
                                                                                            variant="outlined"
                                                                                            size="small"
                                                                                            disabled={
                                                                                                readonly
                                                                                            }
                                                                                        >
                                                                                            <MenuItem value="">
                                                                                                {t(
                                                                                                    "whatsappApplicationOutcome_EMPTY"
                                                                                                )}
                                                                                            </MenuItem>
                                                                                            <MenuItem value="QUALIFIED">
                                                                                                {t(
                                                                                                    "whatsappApplicationOutcome_QUALIFIED"
                                                                                                )}
                                                                                            </MenuItem>
                                                                                            <MenuItem value="NOT_QUALIFIED">
                                                                                                {t(
                                                                                                    "whatsappApplicationOutcome_NOT_QUALIFIED"
                                                                                                )}
                                                                                            </MenuItem>
                                                                                        </TextField>
                                                                                    </Grid>
                                                                                )}
                                                                                {node.conditions.map(
                                                                                    (
                                                                                        condition,
                                                                                        index
                                                                                    ) => (
                                                                                        <Grid
                                                                                            item
                                                                                            xs={
                                                                                                12
                                                                                            }
                                                                                            key={`${node.editorKey}-${index}`}
                                                                                        >
                                                                                            <Grid
                                                                                                container
                                                                                                spacing={
                                                                                                    1
                                                                                                }
                                                                                                alignItems="center"
                                                                                            >
                                                                                                <Grid
                                                                                                    item
                                                                                                    xs={
                                                                                                        12
                                                                                                    }
                                                                                                    md={
                                                                                                        3
                                                                                                    }
                                                                                                >
                                                                                                    <TextField
                                                                                                        select
                                                                                                        fullWidth
                                                                                                        label={t(
                                                                                                            "whatsappApplicationConditionOperator"
                                                                                                        )}
                                                                                                        value={
                                                                                                            condition.operator
                                                                                                        }
                                                                                                        onChange={(
                                                                                                            event
                                                                                                        ) =>
                                                                                                            updateCondition(
                                                                                                                node.editorKey,
                                                                                                                index,
                                                                                                                {
                                                                                                                    operator: event
                                                                                                                        .target
                                                                                                                        .value as any
                                                                                                                }
                                                                                                            )
                                                                                                        }
                                                                                                        variant="outlined"
                                                                                                        size="small"
                                                                                                        disabled={
                                                                                                            readonly
                                                                                                        }
                                                                                                    >
                                                                                                        {WHATSAPP_APPLICATION_CONDITION_OPERATORS.map(
                                                                                                            (
                                                                                                                operator
                                                                                                            ) => (
                                                                                                                <MenuItem
                                                                                                                    key={
                                                                                                                        operator
                                                                                                                    }
                                                                                                                    value={
                                                                                                                        operator
                                                                                                                    }
                                                                                                                >
                                                                                                                    {t(
                                                                                                                        `whatsappApplicationCondition_${operator}`
                                                                                                                    )}
                                                                                                                </MenuItem>
                                                                                                            )
                                                                                                        )}
                                                                                                    </TextField>
                                                                                                </Grid>
                                                                                                <Grid
                                                                                                    item
                                                                                                    xs={
                                                                                                        12
                                                                                                    }
                                                                                                    md={
                                                                                                        3
                                                                                                    }
                                                                                                >
                                                                                                    <TextField
                                                                                                        fullWidth
                                                                                                        label={t(
                                                                                                            "whatsappApplicationConditionValue"
                                                                                                        )}
                                                                                                        {...getWhatsappPreRegistrationFieldProps(
                                                                                                            {
                                                                                                                field:
                                                                                                                    "conditionValue",
                                                                                                                value:
                                                                                                                    condition.value,
                                                                                                                answerKey:
                                                                                                                    node.answerKey
                                                                                                            }
                                                                                                        )}
                                                                                                        onChange={(
                                                                                                            event
                                                                                                        ) =>
                                                                                                            updateCondition(
                                                                                                                node.editorKey,
                                                                                                                index,
                                                                                                                {
                                                                                                                    value:
                                                                                                                        event
                                                                                                                            .target
                                                                                                                            .value
                                                                                                                }
                                                                                                            )
                                                                                                        }
                                                                                                        variant="outlined"
                                                                                                        size="small"
                                                                                                    />
                                                                                                </Grid>
                                                                                                <Grid
                                                                                                    item
                                                                                                    xs={
                                                                                                        12
                                                                                                    }
                                                                                                    md={
                                                                                                        3
                                                                                                    }
                                                                                                >
                                                                                                    <TextField
                                                                                                        fullWidth
                                                                                                        label={t(
                                                                                                            "whatsappApplicationConditionNextNode"
                                                                                                        )}
                                                                                                        {...getWhatsappPreRegistrationFieldProps(
                                                                                                            {
                                                                                                                field:
                                                                                                                    "nodeReference",
                                                                                                                value:
                                                                                                                    condition.nextNodeId
                                                                                                            }
                                                                                                        )}
                                                                                                        onChange={(
                                                                                                            event
                                                                                                        ) =>
                                                                                                            updateCondition(
                                                                                                                node.editorKey,
                                                                                                                index,
                                                                                                                {
                                                                                                                    nextNodeId:
                                                                                                                        event
                                                                                                                            .target
                                                                                                                            .value
                                                                                                                }
                                                                                                            )
                                                                                                        }
                                                                                                        variant="outlined"
                                                                                                        size="small"
                                                                                                    />
                                                                                                </Grid>
                                                                                                <Grid
                                                                                                    item
                                                                                                    xs={
                                                                                                        12
                                                                                                    }
                                                                                                    md={
                                                                                                        1
                                                                                                    }
                                                                                                >
                                                                                                    <TextField
                                                                                                        fullWidth
                                                                                                        type="number"
                                                                                                        label={t(
                                                                                                            "whatsappApplicationConditionScore"
                                                                                                        )}
                                                                                                        value={
                                                                                                            condition.scoreDelta ??
                                                                                                            ""
                                                                                                        }
                                                                                                        onChange={(
                                                                                                            event
                                                                                                        ) =>
                                                                                                            updateCondition(
                                                                                                                node.editorKey,
                                                                                                                index,
                                                                                                                {
                                                                                                                    scoreDelta:
                                                                                                                        event
                                                                                                                            .target
                                                                                                                            .value ===
                                                                                                                        ""
                                                                                                                            ? undefined
                                                                                                                            : Number(
                                                                                                                                  event
                                                                                                                                      .target
                                                                                                                                      .value
                                                                                                                              )
                                                                                                                }
                                                                                                            )
                                                                                                        }
                                                                                                        variant="outlined"
                                                                                                        size="small"
                                                                                                        disabled={
                                                                                                            readonly
                                                                                                        }
                                                                                                    />
                                                                                                </Grid>
                                                                                                <Grid
                                                                                                    item
                                                                                                    xs={
                                                                                                        12
                                                                                                    }
                                                                                                    md={
                                                                                                        2
                                                                                                    }
                                                                                                >
                                                                                                    <FormControlLabel
                                                                                                        control={
                                                                                                            <Checkbox
                                                                                                                checked={
                                                                                                                    condition.eliminatory ===
                                                                                                                    true
                                                                                                                }
                                                                                                                onChange={(
                                                                                                                    event
                                                                                                                ) =>
                                                                                                                    updateCondition(
                                                                                                                        node.editorKey,
                                                                                                                        index,
                                                                                                                        {
                                                                                                                            eliminatory:
                                                                                                                                event
                                                                                                                                    .target
                                                                                                                                    .checked
                                                                                                                        }
                                                                                                                    )
                                                                                                                }
                                                                                                                color="primary"
                                                                                                                disabled={
                                                                                                                    readonly
                                                                                                                }
                                                                                                            />
                                                                                                        }
                                                                                                        label={t(
                                                                                                            "whatsappApplicationConditionEliminatory"
                                                                                                        )}
                                                                                                    />
                                                                                                </Grid>
                                                                                                <Grid
                                                                                                    item
                                                                                                    xs={
                                                                                                        12
                                                                                                    }
                                                                                                    md={
                                                                                                        1
                                                                                                    }
                                                                                                >
                                                                                                    <Tooltip
                                                                                                        title={t(
                                                                                                            "whatsappApplicationRemoveCondition"
                                                                                                        )}
                                                                                                    >
                                                                                                        <IconButton
                                                                                                            onClick={() =>
                                                                                                                removeCondition(
                                                                                                                    node.editorKey,
                                                                                                                    index
                                                                                                                )
                                                                                                            }
                                                                                                            size="small"
                                                                                                            disabled={
                                                                                                                readonly
                                                                                                            }
                                                                                                        >
                                                                                                            <DeleteIcon fontSize="small" />
                                                                                                        </IconButton>
                                                                                                    </Tooltip>
                                                                                                </Grid>
                                                                                            </Grid>
                                                                                        </Grid>
                                                                                    )
                                                                                )}
                                                                                <Grid
                                                                                    item
                                                                                    xs={
                                                                                        12
                                                                                    }
                                                                                >
                                                                                    <Button
                                                                                        onClick={() =>
                                                                                            addCondition(
                                                                                                node.editorKey
                                                                                            )
                                                                                        }
                                                                                        startIcon={
                                                                                            <AddIcon />
                                                                                        }
                                                                                        size="small"
                                                                                        disabled={
                                                                                            readonly
                                                                                        }
                                                                                    >
                                                                                        {t(
                                                                                            "whatsappApplicationAddCondition"
                                                                                        )}
                                                                                    </Button>
                                                                                </Grid>
                                                                            </Grid>
                                                                        </Box>
                                                                    </Paper>
                                                                </Box>
                                                            )}
                                                        </Draggable>
                                                    )
                                                )}
                                                {droppableProvided.placeholder}
                                            </Box>
                                        )}
                                    </Droppable>
                                </DragDropContext>
                            </Grid>
                            <Grid item xs={12}>
                                <Button
                                    onClick={addNode}
                                    startIcon={<AddIcon />}
                                    size="small"
                                    disabled={readonly}
                                >
                                    {t("whatsappApplicationAddNode")}
                                </Button>
                            </Grid>
                            <Grid item xs={12}>
                                <Box display="flex" justifyContent="flex-end">
                                    <Button
                                        onClick={() => saveFlow()}
                                        disabled={
                                            flowOperationPending || readonly
                                        }
                                        color="primary"
                                    >
                                        {t("whatsappApplicationSave")}
                                    </Button>
                                    <Button
                                        onClick={publishFlow}
                                        disabled={
                                            flowOperationPending || readonly
                                        }
                                        color="primary"
                                        variant="contained"
                                    >
                                        {t("whatsappApplicationPublish")}
                                    </Button>
                                </Box>
                            </Grid>
                        </>
                    )}
                </Grid>
            </Paper>
            <WhatsappApplicationHelpDialog
                t={t}
                open={helpOpen}
                scope={flow.scope}
                onClose={() => setHelpOpen(false)}
            />
        </>
    );
};

export default WhatsappApplicationPanel;
